import { Link } from "react-router-dom";
import { useI18n } from "i18n";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Alert } from "@remo-co/ui-core/src/components/Alert";
import { useSelector } from "react-redux";
import { selectCurrentEventId } from "modules/event/selectors";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import {
  getRemainingTrialDays,
  isTrialCompleted,
} from "modules/companyPlanSettings/utils/trial";
import { selectIsMobile } from "modules/deviceInfo";
import { selectUser } from "modules/auth/redux/selectors";
import { styles } from "./styles";

const TrialBanner = (): JSX.Element | null => {
  const isMobileView = useSelector(selectIsMobile);
  const user = useSelector(selectUser);
  const { t } = useI18n(["common", "url"]);
  const { myCompanyPlan, eventCompanyPlan } = useCompanyPlanSettings();
  const currentEventId = useSelector(selectCurrentEventId);
  // If event company plan, then user is in event view else check current company plan
  const trialLeft = getRemainingTrialDays(
    currentEventId ? eventCompanyPlan : myCompanyPlan,
  );
  const paymentFailedAt = myCompanyPlan && myCompanyPlan.paymentFailedAt;
  const myTrialExpired = isTrialCompleted(myCompanyPlan);

  if (paymentFailedAt) {
    return (
      <Alert
        severity="error"
        action={
          <Link
            to={{
              pathname: "/account-settings/billing",
              state: { history: true },
            }}
          >
            {t("common:view.billing.history")}
          </Link>
        }
      >
        {t("common:payment.failed.alert")}
      </Alert>
    );
  }

  const keys = user
    ? {
        email: user.email,
        firstName: user.profile.firstName,
        lastName: user.profile.lastName,
      }
    : undefined;

  if (
    !isMobileView &&
    ((trialLeft !== null && trialLeft !== undefined) || myTrialExpired)
  ) {
    return (
      <Alert
        severity="warning"
        action={
          /** One edge case: if viewer company is in trial and event company also in trial, this will show in event view */
          <a
            href={t("url:upgrade.now.form", keys)}
            target="_blank"
            rel="noreferrer"
            data-testid="upgrade-now-link"
          >
            <Typography component="span">{t("common:upgrade.now")}</Typography>
          </a>
        }
        sx={styles.upgradeNow}
      >
        {!trialLeft || trialLeft <= 0 || myTrialExpired ? (
          <Typography component="span" color="inherit">
            {t("common:your.trial.expired")}
          </Typography>
        ) : (
          <Typography component="span" color="inherit">
            {t("common:trial.end.timer", {
              key: trialLeft,
            })}
          </Typography>
        )}
      </Alert>
    );
  }

  return null;
};

export default TrialBanner;
